/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { saveAs } from 'file-saver';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, Filler } from 'chart.js';
import jsPDF from 'jspdf';
import FacilityService from "../../services/FacilityService";
import AssetService from "../../services/AssetService";
import ReportService from "../../services/ReportService";
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import util from "../../utils/util";
import moment from "moment";
import PdfReportsUtil from "../../utils/pdfUtils/PdfReportsUtil";
import excel from "../assets/icons/excel.png"; 
import acrobat from "../assets/icons/acrobat.png";
import search from "../assets/icons/magnifying-glass-solid.svg";
import {
    Button,
    message,
    Card,
    Row,
    Col,
    Statistic,
    Switch,
    Progress,
    Tag,
    Table,
    Dropdown,
    Space,
    Tabs,
    Badge
} from 'antd';
import {
    BarChartOutlined,
    ArrowUpOutlined,
    SyncOutlined,
    DashboardOutlined,
    AlertOutlined,
    ClockCircleOutlined,
    DownloadOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    CarOutlined,
    MedicineBoxOutlined,
    AppstoreOutlined,
    InboxOutlined,
    ToolOutlined
} from '@ant-design/icons';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, Filler);

// ── Category icon helper ─────────────────────────────────────────────────────
const getCatIcon = (cat) => {
    if (!cat) return <AppstoreOutlined />;
    const c = cat.toLowerCase();
    if (c.includes('vehicle')) return <CarOutlined />;
    if (c.includes('medical') || c.includes('med')) return <MedicineBoxOutlined />;
    if (c.includes('supply') || c.includes('supplies')) return <InboxOutlined />;
    if (c.includes('equipment') || c.includes('non-med')) return <ToolOutlined />;
    return <AppstoreOutlined />;
};

// Utility function to export chart as JPG
const exportChartAsJPG = (chartRef, filename) => {
    if (chartRef.current) {
        const canvas = chartRef.current.canvas;
        canvas.toBlob((blob) => {
            saveAs(blob, `${filename}.jpg`);
        }, 'image/jpeg', 0.8);
    }
};

// Utility function to generate PDF from multiple charts
const generateAnalyticsPDF = (chartsData, title) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(title, 20, 20);
    
    let yPosition = 40;
    chartsData.forEach((chartData, index) => {
        if (chartData.canvas) {
            const imgData = chartData.canvas.toDataURL('image/jpeg', 0.8);
            doc.addImage(imgData, 'JPEG', 20, yPosition, 170, 100);
            yPosition += 120;
            
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }
        }
    });
    
    doc.save(`${title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
};

// Transaction Volume Analytics Component
const TransactionVolumeAnalytics = ({ data, isVisible }) => {
    const [volumeMetrics, setVolumeMetrics] = useState({
        dailyTrends: [],
        actionDistribution: {},
        facilityActivity: {},
        categoryPatterns: {}
    });

    // Chart refs for export functionality
    const dailyTrendsRef = useRef();
    const actionDistributionRef = useRef();
    const facilityActivityRef = useRef();

    useEffect(() => {
        if (data && data.length > 0) {
            calculateVolumeMetrics();
        }
    }, [data]);

    const calculateVolumeMetrics = () => {
        const dailyTrends = {};
        const actionDistribution = {};
        const facilityActivity = {};
        const categoryPatterns = {};

        data.forEach(transaction => {
            const date = moment(transaction.txn_date).format('YYYY-MM-DD');
            dailyTrends[date] = (dailyTrends[date] || 0) + 1;

            const action = transaction.action || 'Unknown';
            actionDistribution[action] = (actionDistribution[action] || 0) + 1;

            const facility = transaction.client || 'Unknown';
            facilityActivity[facility] = (facilityActivity[facility] || 0) + 1;

            const category = transaction.cat || 'Unknown';
            categoryPatterns[category] = (categoryPatterns[category] || 0) + 1;
        });

        const sortedDailyTrends = Object.keys(dailyTrends)
            .sort()
            .slice(-30)
            .map(date => ({
                date: moment(date).format('MMM DD'),
                count: dailyTrends[date]
            }));

        setVolumeMetrics({
            dailyTrends: sortedDailyTrends,
            actionDistribution,
            facilityActivity,
            categoryPatterns
        });
    };

    // Export functions
    const exportDailyTrendsJPG = () => {
        exportChartAsJPG(dailyTrendsRef, 'daily-transaction-trends');
    };

    const exportActionDistributionJPG = () => {
        exportChartAsJPG(actionDistributionRef, 'action-distribution');
    };

    const exportFacilityActivityJPG = () => {
        exportChartAsJPG(facilityActivityRef, 'facility-activity');
    };

    const exportAllVolumeAnalyticsPDF = () => {
        const chartsData = [
            { canvas: dailyTrendsRef.current?.canvas },
            { canvas: actionDistributionRef.current?.canvas },
            { canvas: facilityActivityRef.current?.canvas }
        ];
        generateAnalyticsPDF(chartsData, 'Transaction Volume Analytics');
    };

    // Chart configurations
    const dailyTrendsChart = {
        labels: volumeMetrics.dailyTrends.map(d => d.date),
        datasets: [{
            label: 'Transactions',
            data: volumeMetrics.dailyTrends.map(d => d.count),
            borderColor: '#1890ff',
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            fill: false,
            tension: 0.4
        }]
    };

    const actionDistributionChart = {
        labels: Object.keys(volumeMetrics.actionDistribution),
        datasets: [{
            data: Object.values(volumeMetrics.actionDistribution),
            backgroundColor: [
                '#1890ff', '#52c41a', '#faad14', '#f5222d', 
                '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'
            ],
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    const facilityActivityChart = {
        labels: Object.keys(volumeMetrics.facilityActivity).slice(0, 10),
        datasets: [{
            label: 'Transactions',
            data: Object.values(volumeMetrics.facilityActivity).slice(0, 10),
            backgroundColor: 'rgba(82, 196, 26, 0.6)',
            borderColor: '#52c41a',
            borderWidth: 1
        }]
    };

    const exportMenuItems = [
        {
            key: 'export-all-pdf',
            icon: <FilePdfOutlined />,
            label: 'Export All as PDF',
            onClick: exportAllVolumeAnalyticsPDF
        },
        {
            type: 'divider'
        },
        {
            key: 'daily-trends-jpg',
            icon: <FileImageOutlined />,
            label: 'Daily Trends (JPG)',
            onClick: exportDailyTrendsJPG
        },
        {
            key: 'action-dist-jpg',
            icon: <FileImageOutlined />,
            label: 'Action Distribution (JPG)',
            onClick: exportActionDistributionJPG
        },
        {
            key: 'facility-activity-jpg',
            icon: <FileImageOutlined />,
            label: 'Facility Activity (JPG)',
            onClick: exportFacilityActivityJPG
        }
    ];

    if (!isVisible) return null;

    return (
        <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                    <Button type="primary" size="small">
                        <Space>
                            <DownloadOutlined />
                            Export Charts
                        </Space>
                    </Button>
                </Dropdown>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Total Transactions"
                            value={data.length}
                            prefix={<SyncOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Daily Average"
                            value={volumeMetrics.dailyTrends.length > 0 ? 
                                Math.round(volumeMetrics.dailyTrends.reduce((sum, d) => sum + d.count, 0) / volumeMetrics.dailyTrends.length) : 0}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Active Facilities"
                            value={Object.keys(volumeMetrics.facilityActivity).length}
                            prefix={<DashboardOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Categories Involved"
                            value={Object.keys(volumeMetrics.categoryPatterns).length}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card 
                        title="Daily Transaction Trends (Last 30 Days)" 
                        size="small"
                        extra={
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={exportDailyTrendsJPG}
                                title="Export as JPG"
                            />
                        }
                    >
                        <div style={{ height: '300px' }}>
                            <Line 
                                ref={dailyTrendsRef}
                                data={dailyTrendsChart} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false }
                                    },
                                    scales: {
                                        y: { beginAtZero: true }
                                    }
                                }} 
                            />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card 
                        title="Transaction Actions Distribution" 
                        size="small"
                        extra={
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={exportActionDistributionJPG}
                                title="Export as JPG"
                            />
                        }
                    >
                        <div style={{ height: '300px' }}>
                            <Doughnut 
                                ref={actionDistributionRef}
                                data={actionDistributionChart} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'bottom' }
                                    }
                                }} 
                            />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col span={24}>
                    <Card 
                        title="Top 10 Most Active Facilities" 
                        size="small"
                        extra={
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={exportFacilityActivityJPG}
                                title="Export as JPG"
                            />
                        }
                    >
                        <div style={{ height: '300px' }}>
                            <Bar 
                                ref={facilityActivityRef}
                                data={facilityActivityChart} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false }
                                    },
                                    scales: {
                                        y: { beginAtZero: true }
                                    }
                                }} 
                            />
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
// Stock Movement Efficiency Analytics Component
const StockMovementAnalytics = ({ data, isVisible }) => {
    const [movementMetrics, setMovementMetrics] = useState({
        stockVelocity: [],
        transferPatterns: {},
        stockLevelChanges: [],
        efficiencyScores: {}
    });

    const velocityRef = useRef();
    const transferRef = useRef();

    useEffect(() => {
        if (data && data.length > 0) {
            calculateMovementMetrics();
        }
    }, [data]);

    const calculateMovementMetrics = () => {
        const itemActivity = {};
        const transferPatterns = {};
        const stockChanges = [];
        
        data.forEach((transaction, index) => {
            const item = transaction.item_full_name || 'Unknown Item';
            const action = transaction.action || 'Unknown';
            
            if (!itemActivity[item]) {
                itemActivity[item] = {
                    transactions: 0,
                    totalQtyChange: 0,
                    lastTransaction: transaction.txn_date
                };
            }
            itemActivity[item].transactions++;
            itemActivity[item].totalQtyChange += Math.abs(transaction.qty || 0);
            
            if (action.includes('Transfer') || action.includes('Received')) {
                transferPatterns[action] = (transferPatterns[action] || 0) + 1;
            }
            
            const qtyChange = Math.abs(transaction.qty || 0);
            if (qtyChange > 0) {
                stockChanges.push({
                    id: `change-${index}-${transaction.txn_date || Date.now()}`,
                    item,
                    change: qtyChange,
                    action,
                    date: transaction.txn_date,
                    before: transaction.stock_before || 0,
                    after: transaction.stock_after || 0
                });
            }
        });

        const stockVelocity = Object.entries(itemActivity)
            .map(([item, activity]) => ({
                item: item.length > 30 ? item.substring(0, 30) + '...' : item,
                velocity: activity.transactions,
                totalMovement: activity.totalQtyChange
            }))
            .sort((a, b) => b.velocity - a.velocity)
            .slice(0, 15);

        const efficiencyScores = {};
        Object.entries(itemActivity).forEach(([item, activity]) => {
            const efficiency = activity.transactions > 0 ? 
                (activity.totalQtyChange / activity.transactions) : 0;
            efficiencyScores[item] = Math.round(efficiency * 100) / 100;
        });

        setMovementMetrics({
            stockVelocity,
            transferPatterns,
            stockLevelChanges: stockChanges.slice(0, 20),
            efficiencyScores
        });
    };

    const exportVelocityJPG = () => {
        exportChartAsJPG(velocityRef, 'stock-velocity');
    };

    const exportTransferJPG = () => {
        exportChartAsJPG(transferRef, 'transfer-patterns');
    };

    const exportAllMovementAnalyticsPDF = () => {
        const chartsData = [
            { canvas: velocityRef.current?.canvas },
            { canvas: transferRef.current?.canvas }
        ];
        generateAnalyticsPDF(chartsData, 'Stock Movement Analytics');
    };

    const velocityChart = {
        labels: movementMetrics.stockVelocity.map(item => item.item),
        datasets: [{
            label: 'Transaction Count',
            data: movementMetrics.stockVelocity.map(item => item.velocity),
            backgroundColor: 'rgba(114, 46, 209, 0.6)',
            borderColor: '#722ed1',
            borderWidth: 1
        }]
    };

    const transferChart = {
        labels: Object.keys(movementMetrics.transferPatterns),
        datasets: [{
            data: Object.values(movementMetrics.transferPatterns),
            backgroundColor: ['#52c41a', '#1890ff', '#faad14', '#f5222d'],
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    const exportMenuItems = [
        {
            key: 'export-all-pdf',
            icon: <FilePdfOutlined />,
            label: 'Export All as PDF',
            onClick: exportAllMovementAnalyticsPDF
        },
        { type: 'divider' },
        {
            key: 'velocity-jpg',
            icon: <FileImageOutlined />,
            label: 'Stock Velocity (JPG)',
            onClick: exportVelocityJPG
        },
        {
            key: 'transfer-jpg',
            icon: <FileImageOutlined />,
            label: 'Transfer Patterns (JPG)',
            onClick: exportTransferJPG
        }
    ];

    if (!isVisible) return null;

    return (
        <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                    <Button type="primary" size="small">
                        <Space>
                            <DownloadOutlined />
                            Export Charts
                        </Space>
                    </Button>
                </Dropdown>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="High Velocity Items"
                            value={movementMetrics.stockVelocity.filter(item => item.velocity > 5).length}
                            prefix={<ArrowUpOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Transfer Operations"
                            value={Object.values(movementMetrics.transferPatterns).reduce((sum, val) => sum + val, 0)}
                            prefix={<SyncOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Avg Stock Change"
                            value={movementMetrics.stockLevelChanges.length > 0 ? 
                                Math.round(movementMetrics.stockLevelChanges.reduce((sum, change) => sum + change.change, 0) / movementMetrics.stockLevelChanges.length) : 0}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Items Tracked"
                            value={Object.keys(movementMetrics.efficiencyScores).length}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card 
                        title="Top 15 Items by Stock Velocity" 
                        size="small"
                        extra={
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={exportVelocityJPG}
                                title="Export as JPG"
                            />
                        }
                    >
                        <div style={{ height: '350px' }}>
                            <Bar 
                                ref={velocityRef}
                                data={velocityChart} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false }
                                    },
                                    scales: {
                                        y: { beginAtZero: true },
                                        x: { 
                                            ticks: { 
                                                maxRotation: 45,
                                                minRotation: 45 
                                            }
                                        }
                                    }
                                }} 
                            />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card 
                        title="Transfer Patterns" 
                        size="small"
                        extra={
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<DownloadOutlined />}
                                onClick={exportTransferJPG}
                                title="Export as JPG"
                            />
                        }
                    >
                        <div style={{ height: '350px' }}>
                            <Pie 
                                ref={transferRef}
                                data={transferChart} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'bottom' }
                                    }
                                }} 
                            />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col span={24}>
                    <Card title="Recent Significant Stock Changes" size="small">
                        <Table
                            dataSource={movementMetrics.stockLevelChanges}
                            pagination={false}
                            size="small"
                            scroll={{ y: 200 }}
                            rowKey="id"
                            columns={[
                                {
                                    title: 'Item',
                                    dataIndex: 'item',
                                    key: 'item',
                                    width: 200,
                                    ellipsis: true
                                },
                                {
                                    title: 'Action',
                                    dataIndex: 'action',
                                    key: 'action',
                                    width: 150
                                },
                                {
                                    title: 'Quantity Change',
                                    dataIndex: 'change',
                                    key: 'change',
                                    width: 120,
                                    render: (value) => (
                                        <Tag color={value > 50 ? 'red' : value > 20 ? 'orange' : 'green'}>
                                            {value}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Before',
                                    dataIndex: 'before',
                                    key: 'before',
                                    width: 80
                                },
                                {
                                    title: 'After',
                                    dataIndex: 'after',
                                    key: 'after',
                                    width: 80
                                },
                                {
                                    title: 'Date',
                                    dataIndex: 'date',
                                    key: 'date',
                                    width: 100,
                                    render: (date) => moment(date).format('MMM DD')
                                }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
// Data Capturer Performance Analytics Component  
const DataCapturerPerformanceAnalytics = ({ data, isVisible }) => {
    const [performanceMetrics, setPerformanceMetrics] = useState({
        userStats: [],
        productivityTrends: [],
        accuracyMetrics: {},
        workPatterns: {},
        performanceScores: {}
    });

    // Chart refs for export functionality
    const productivityRef = useRef();
    const accuracyRef = useRef();
    const workPatternsRef = useRef();
    const performanceScoreRef = useRef();

    useEffect(() => {
        if (data && data.length > 0) {
            calculatePerformanceMetrics();
        }
    }, [data]);

    const calculatePerformanceMetrics = () => {
        const userActivity = {};
        const dailyActivity = {};
        const hourlyActivity = {};
        const errorPatterns = {};
        
        data.forEach(transaction => {
            const user = transaction.created_by_name || 'Unknown User';
            const userId = transaction.created_by_user || user;
            const date = moment(transaction.created_at || transaction.txn_date).format('YYYY-MM-DD');
            const hour = moment(transaction.created_at || transaction.txn_date).hour();
            const dayOfWeek = moment(transaction.created_at || transaction.txn_date).format('dddd');
            
            if (!userActivity[user]) {
                userActivity[user] = {
                    totalTransactions: 0,
                    uniqueDays: new Set(),
                    actions: {},
                    facilities: new Set(),
                    avgTransactionsPerDay: 0,
                    errorCount: 0,
                    consistencyScore: 0,
                    lastActivity: transaction.created_at || transaction.txn_date,
                    firstActivity: transaction.created_at || transaction.txn_date
                };
            }

            userActivity[user].totalTransactions++;
            userActivity[user].uniqueDays.add(date);
            userActivity[user].actions[transaction.action] = (userActivity[user].actions[transaction.action] || 0) + 1;
            if (transaction.client) {
                userActivity[user].facilities.add(transaction.client);
            }
            
            const expectedChange = transaction.qty || 0;
            const actualChange = (transaction.stock_after || 0) - (transaction.stock_before || 0);
            if (Math.abs(expectedChange - actualChange) > 0.1) {
                userActivity[user].errorCount++;
                errorPatterns[user] = (errorPatterns[user] || 0) + 1;
            }

            const dailyKey = `${date}-${user}`;
            dailyActivity[dailyKey] = (dailyActivity[dailyKey] || 0) + 1;

            if (!hourlyActivity[user]) {
                hourlyActivity[user] = {};
            }
            hourlyActivity[user][hour] = (hourlyActivity[user][hour] || 0) + 1;

            const currentDate = moment(transaction.created_at || transaction.txn_date);
            if (currentDate.isAfter(moment(userActivity[user].lastActivity))) {
                userActivity[user].lastActivity = transaction.created_at || transaction.txn_date;
            }
            if (currentDate.isBefore(moment(userActivity[user].firstActivity))) {
                userActivity[user].firstActivity = transaction.created_at || transaction.txn_date;
            }
        });

        const userStats = Object.entries(userActivity).map(([user, stats]) => {
            const uniqueDaysCount = stats.uniqueDays.size;
            const avgPerDay = uniqueDaysCount > 0 ? Math.round((stats.totalTransactions / uniqueDaysCount) * 100) / 100 : 0;
            const errorRate = stats.totalTransactions > 0 ? Math.round((stats.errorCount / stats.totalTransactions) * 10000) / 100 : 0;
            const facilityCount = stats.facilities.size;
            
            const dailyTxns = Array.from(stats.uniqueDays).map(date => {
                const dailyKey = `${date}-${user}`;
                return dailyActivity[dailyKey] || 0;
            });
            const avgDaily = dailyTxns.reduce((sum, val) => sum + val, 0) / dailyTxns.length;
            const variance = dailyTxns.length > 1 ? 
                dailyTxns.reduce((sum, val) => sum + Math.pow(val - avgDaily, 2), 0) / dailyTxns.length : 0;
            const consistencyScore = Math.max(0, 100 - (Math.sqrt(variance) / avgDaily * 100));

            const performanceScore = Math.round(
                (avgPerDay * 0.4) + 
                (Math.max(0, 100 - errorRate) * 0.3) + 
                (Math.min(100, facilityCount * 10) * 0.2) + 
                (consistencyScore * 0.1)
            );

            return {
                user: user.length > 25 ? user.substring(0, 25) + '...' : user,
                fullName: user,
                totalTransactions: stats.totalTransactions,
                activeDays: uniqueDaysCount,
                avgPerDay,
                errorRate,
                facilityCount,
                consistencyScore: Math.round(consistencyScore * 100) / 100,
                performanceScore: Math.min(100, performanceScore),
                lastActivity: stats.lastActivity,
                topAction: Object.entries(stats.actions).sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A',
                workingDays: moment(stats.lastActivity).diff(moment(stats.firstActivity), 'days') + 1
            };
        }).sort((a, b) => b.performanceScore - a.performanceScore);

        const last30Days = Array.from({ length: 30 }, (_, i) => 
            moment().subtract(29 - i, 'days').format('YYYY-MM-DD')
        );
        
        const productivityTrends = last30Days.map(date => {
            const dayTotal = Object.keys(dailyActivity)
                .filter(key => key.startsWith(date))
                .reduce((sum, key) => sum + dailyActivity[key], 0);
            return {
                date: moment(date).format('MMM DD'),
                fullDate: date,
                transactions: dayTotal
            };
        });

        const workPatterns = {};
        Object.entries(hourlyActivity).forEach(([user, hours]) => {
            const peakHour = Object.entries(hours).sort(([,a], [,b]) => b - a)[0];
            workPatterns[user] = {
                peakHour: peakHour ? `${peakHour[0]}:00` : 'N/A',
                peakCount: peakHour ? peakHour[1] : 0,
                totalHours: Object.keys(hours).length,
                avgPerHour: Object.values(hours).reduce((sum, val) => sum + val, 0) / Object.keys(hours).length
            };
        });

        setPerformanceMetrics({
            userStats: userStats.slice(0, 20),
            productivityTrends,
            accuracyMetrics: errorPatterns,
            workPatterns,
            performanceScores: userStats.reduce((acc, user) => {
                acc[user.fullName] = user.performanceScore;
                return acc;
            }, {})
        });
    };

    const exportProductivityJPG = () => exportChartAsJPG(productivityRef, 'productivity-trends');
    const exportAccuracyJPG = () => exportChartAsJPG(accuracyRef, 'accuracy-metrics');
    const exportWorkPatternsJPG = () => exportChartAsJPG(workPatternsRef, 'work-patterns');
    const exportPerformanceScoreJPG = () => exportChartAsJPG(performanceScoreRef, 'performance-scores');

    const exportAllPerformanceAnalyticsPDF = () => {
        const chartsData = [
            { canvas: productivityRef.current?.canvas },
            { canvas: accuracyRef.current?.canvas },
            { canvas: workPatternsRef.current?.canvas },
            { canvas: performanceScoreRef.current?.canvas }
        ];
        generateAnalyticsPDF(chartsData, 'Data Capturer Performance Analytics');
    };

    const productivityTrendsChart = {
        labels: performanceMetrics.productivityTrends.map(d => d.date),
        datasets: [{
            label: 'Daily Transactions',
            data: performanceMetrics.productivityTrends.map(d => d.transactions),
            borderColor: '#52c41a',
            backgroundColor: 'rgba(82, 196, 26, 0.1)',
            fill: true,
            tension: 0.4
        }]
    };

    const accuracyChart = {
        labels: Object.keys(performanceMetrics.accuracyMetrics).slice(0, 10),
        datasets: [{
            label: 'Error Count',
            data: Object.values(performanceMetrics.accuracyMetrics).slice(0, 10),
            backgroundColor: 'rgba(245, 34, 45, 0.6)',
            borderColor: '#f5222d',
            borderWidth: 1
        }]
    };

    const performanceScoreChart = {
        labels: performanceMetrics.userStats.slice(0, 10).map(u => u.user),
        datasets: [{
            label: 'Performance Score',
            data: performanceMetrics.userStats.slice(0, 10).map(u => u.performanceScore),
            backgroundColor: performanceMetrics.userStats.slice(0, 10).map(u => 
                u.performanceScore >= 80 ? 'rgba(82, 196, 26, 0.6)' :
                u.performanceScore >= 60 ? 'rgba(250, 173, 20, 0.6)' :
                'rgba(245, 34, 45, 0.6)'
            ),
            borderColor: performanceMetrics.userStats.slice(0, 10).map(u => 
                u.performanceScore >= 80 ? '#52c41a' :
                u.performanceScore >= 60 ? '#faad14' :
                '#f5222d'
            ),
            borderWidth: 1
        }]
    };

    const workPatternsChart = {
        labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
        datasets: [{
            label: 'Transaction Count',
            data: Array.from({ length: 24 }, (_, hour) => {
                return Object.values(performanceMetrics.workPatterns)
                    .filter(pattern => pattern.peakHour === `${hour}:00`)
                    .reduce((sum, pattern) => sum + pattern.peakCount, 0);
            }),
            backgroundColor: 'rgba(24, 144, 255, 0.6)',
            borderColor: '#1890ff',
            borderWidth: 1
        }]
    };

    const exportMenuItems = [
        { key: 'export-all-pdf', icon: <FilePdfOutlined />, label: 'Export All as PDF', onClick: exportAllPerformanceAnalyticsPDF },
        { type: 'divider' },
        { key: 'productivity-jpg', icon: <FileImageOutlined />, label: 'Productivity Trends (JPG)', onClick: exportProductivityJPG },
        { key: 'accuracy-jpg', icon: <FileImageOutlined />, label: 'Accuracy Metrics (JPG)', onClick: exportAccuracyJPG },
        { key: 'work-patterns-jpg', icon: <FileImageOutlined />, label: 'Work Patterns (JPG)', onClick: exportWorkPatternsJPG },
        { key: 'performance-score-jpg', icon: <FileImageOutlined />, label: 'Performance Scores (JPG)', onClick: exportPerformanceScoreJPG }
    ];

    if (!isVisible) return null;

    return (
        <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                    <Button type="primary" size="small">
                        <Space><DownloadOutlined />Export Charts</Space>
                    </Button>
                </Dropdown>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Active Data Capturers" value={performanceMetrics.userStats.length} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#1890ff' }} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Top Performer Score" value={performanceMetrics.userStats.length > 0 ? performanceMetrics.userStats[0].performanceScore : 0} suffix="/100" valueStyle={{ color: '#52c41a' }} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Avg Daily Productivity" value={performanceMetrics.userStats.length > 0 ? Math.round(performanceMetrics.userStats.reduce((sum, u) => sum + u.avgPerDay, 0) / performanceMetrics.userStats.length) : 0} valueStyle={{ color: '#722ed1' }} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Users with Errors" value={Object.keys(performanceMetrics.accuracyMetrics).length} valueStyle={{ color: '#f5222d' }} />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card title="Overall Productivity Trends (Last 30 Days)" size="small" extra={<Button type="text" size="small" icon={<DownloadOutlined />} onClick={exportProductivityJPG} title="Export as JPG" />}>
                        <div style={{ height: '300px' }}>
                            <Line ref={productivityRef} data={productivityTrendsChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card title="Top 10 Performance Scores" size="small" extra={<Button type="text" size="small" icon={<DownloadOutlined />} onClick={exportPerformanceScoreJPG} title="Export as JPG" />}>
                        <div style={{ height: '300px' }}>
                            <Bar ref={performanceScoreRef} data={performanceScoreChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, max: 100 }, x: { ticks: { maxRotation: 45, minRotation: 45 } } } }} />
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col xs={24} lg={12}>
                    <Card title="Work Hour Patterns" size="small" extra={<Button type="text" size="small" icon={<DownloadOutlined />} onClick={exportWorkPatternsJPG} title="Export as JPG" />}>
                        <div style={{ height: '300px' }}>
                            <Bar ref={workPatternsRef} data={workPatternsChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Users with Data Entry Errors" size="small" extra={<Button type="text" size="small" icon={<DownloadOutlined />} onClick={exportAccuracyJPG} title="Export as JPG" />}>
                        {Object.keys(performanceMetrics.accuracyMetrics).length > 0 ? (
                            <div style={{ height: '300px' }}>
                                <Bar ref={accuracyRef} data={accuracyChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true }, x: { ticks: { maxRotation: 45, minRotation: 45 } } } }} />
                            </div>
                        ) : (
                            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#52c41a', fontSize: '16px', fontWeight: 'bold' }}>
                                No data entry errors detected!<br />
                                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                                    All data capturers are maintaining accuracy standards.
                                </span>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col span={24}>
                    <Card title="Detailed Performance Metrics" size="small">
                        <Table
                            dataSource={performanceMetrics.userStats}
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            size="small"
                            scroll={{ x: 1000 }}
                            rowKey={(record, index) => `user-${record.fullName || index}`}
                            columns={[
                                { title: 'Data Capturer', dataIndex: 'user', key: 'user', width: 150, fixed: 'left', ellipsis: true, render: (text, record) => <span title={record.fullName}>{text}</span> },
                                { title: 'Performance Score', dataIndex: 'performanceScore', key: 'performanceScore', width: 120, render: (score) => (<div><Progress percent={score} size="small" strokeColor={score >= 80 ? '#52c41a' : score >= 60 ? '#faad14' : '#f5222d'} /><span style={{ fontSize: '12px', color: '#666' }}>{score}/100</span></div>) },
                                { title: 'Total Transactions', dataIndex: 'totalTransactions', key: 'totalTransactions', width: 100, sorter: (a, b) => a.totalTransactions - b.totalTransactions },
                                { title: 'Active Days', dataIndex: 'activeDays', key: 'activeDays', width: 80 },
                                { title: 'Avg/Day', dataIndex: 'avgPerDay', key: 'avgPerDay', width: 80, sorter: (a, b) => a.avgPerDay - b.avgPerDay },
                                { title: 'Error Rate %', dataIndex: 'errorRate', key: 'errorRate', width: 100, render: (rate) => <Tag color={rate === 0 ? 'green' : rate < 5 ? 'orange' : 'red'}>{rate}%</Tag>, sorter: (a, b) => a.errorRate - b.errorRate },
                                { title: 'Facilities', dataIndex: 'facilityCount', key: 'facilityCount', width: 80 },
                                { title: 'Consistency', dataIndex: 'consistencyScore', key: 'consistencyScore', width: 100, render: (score) => `${score}%`, sorter: (a, b) => a.consistencyScore - b.consistencyScore },
                                { title: 'Top Action', dataIndex: 'topAction', key: 'topAction', width: 120, ellipsis: true },
                                { title: 'Last Activity', dataIndex: 'lastActivity', key: 'lastActivity', width: 120, render: (date) => moment(date).format('MMM DD, HH:mm') }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
// Inventory Health Analytics Component
const InventoryHealthAnalytics = ({ data, isVisible }) => {
    const [healthMetrics, setHealthMetrics] = useState({
        stockDiscrepancies: [],
        healthScore: 0,
        alertItems: [],
        userActivity: {}
    });

    const userActivityRef = useRef();

    useEffect(() => {
        if (data && data.length > 0) {
            calculateHealthMetrics();
        }
    }, [data]);

    const calculateHealthMetrics = () => {
        const itemHealth = {};
        const userActivity = {};
        const discrepancies = [];
        
        data.forEach(transaction => {
            const item = transaction.item_full_name || 'Unknown Item';
            const user = transaction.created_by_name || 'Unknown User';
            
            userActivity[user] = (userActivity[user] || 0) + 1;
            
            if (!itemHealth[item]) {
                itemHealth[item] = {
                    transactions: 0,
                    stockVariance: 0,
                    lastStock: transaction.stock_after || 0
                };
            }
            
            itemHealth[item].transactions++;
            const expectedChange = transaction.qty || 0;
            const actualChange = (transaction.stock_after || 0) - (transaction.stock_before || 0);
            
            if (Math.abs(expectedChange - actualChange) > 0.1) {
                discrepancies.push({
                    item,
                    expected: expectedChange,
                    actual: actualChange,
                    difference: Math.abs(expectedChange - actualChange),
                    date: transaction.txn_date,
                    action: transaction.action
                });
            }
            
            itemHealth[item].stockVariance += Math.abs(expectedChange - actualChange);
        });

        const totalItems = Object.keys(itemHealth).length;
        const healthyItems = Object.values(itemHealth).filter(health => 
            health.stockVariance < 5 && health.transactions > 0
        ).length;
        const healthScore = totalItems > 0 ? Math.round((healthyItems / totalItems) * 100) : 0;

        const alertItems = Object.entries(itemHealth)
            .filter(([item, health]) => health.stockVariance > 10 || health.transactions > 20)
            .map(([item, health]) => ({
                item: item.length > 40 ? item.substring(0, 40) + '...' : item,
                variance: Math.round(health.stockVariance * 100) / 100,
                transactions: health.transactions,
                riskLevel: health.stockVariance > 20 ? 'High' : health.stockVariance > 10 ? 'Medium' : 'Low'
            }))
            .sort((a, b) => b.variance - a.variance)
            .slice(0, 10);

        setHealthMetrics({
            stockDiscrepancies: discrepancies.slice(0, 15),
            healthScore,
            alertItems,
            userActivity
        });
    };

    const exportUserActivityJPG = () => exportChartAsJPG(userActivityRef, 'user-activity');

    const exportAllHealthAnalyticsPDF = () => {
        const chartsData = [{ canvas: userActivityRef.current?.canvas }];
        generateAnalyticsPDF(chartsData, 'Inventory Health Analytics');
    };

    const userActivityChart = {
        labels: Object.keys(healthMetrics.userActivity).slice(0, 10),
        datasets: [{
            label: 'Transactions',
            data: Object.values(healthMetrics.userActivity).slice(0, 10),
            backgroundColor: 'rgba(250, 140, 22, 0.6)',
            borderColor: '#fa8c16',
            borderWidth: 1
        }]
    };

    const exportMenuItems = [
        { key: 'export-all-pdf', icon: <FilePdfOutlined />, label: 'Export All as PDF', onClick: exportAllHealthAnalyticsPDF },
        { type: 'divider' },
        { key: 'user-activity-jpg', icon: <FileImageOutlined />, label: 'User Activity (JPG)', onClick: exportUserActivityJPG }
    ];

    if (!isVisible) return null;

    return (
        <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                    <Button type="primary" size="small">
                        <Space><DownloadOutlined />Export Charts</Space>
                    </Button>
                </Dropdown>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Inventory Health Score" value={healthMetrics.healthScore} suffix="%" prefix={<DashboardOutlined />} valueStyle={{ color: healthMetrics.healthScore >= 80 ? '#52c41a' : healthMetrics.healthScore >= 60 ? '#faad14' : '#f5222d' }} />
                        <Progress percent={healthMetrics.healthScore} strokeColor={healthMetrics.healthScore >= 80 ? '#52c41a' : healthMetrics.healthScore >= 60 ? '#faad14' : '#f5222d'} size="small" showInfo={false} style={{ marginTop: '8px' }} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Stock Discrepancies" value={healthMetrics.stockDiscrepancies.length} prefix={<AlertOutlined />} valueStyle={{ color: '#f5222d' }} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="High Risk Items" value={healthMetrics.alertItems.filter(item => item.riskLevel === 'High').length} valueStyle={{ color: '#f5222d' }} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Active Users" value={Object.keys(healthMetrics.userActivity).length} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#722ed1' }} />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card title="Alert Items (High Variance/Activity)" size="small">
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {healthMetrics.alertItems.length > 0 ? healthMetrics.alertItems.map((item, index) => (
                                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: index < healthMetrics.alertItems.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '500', fontSize: '14px' }}>{item.item}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>{item.transactions} transactions</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <Tag color={item.riskLevel === 'High' ? 'red' : item.riskLevel === 'Medium' ? 'orange' : 'green'}>{item.riskLevel}</Tag>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Variance: {item.variance}</div>
                                    </div>
                                </div>
                            )) : (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No high-risk items identified</div>
                            )}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Top Users by Activity" size="small" extra={<Button type="text" size="small" icon={<DownloadOutlined />} onClick={exportUserActivityJPG} title="Export as JPG" />}>
                        <div style={{ height: '300px' }}>
                            <Bar ref={userActivityRef} data={userActivityChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true }, x: { ticks: { maxRotation: 45, minRotation: 45 } } } }} />
                        </div>
                    </Card>
                </Col>
            </Row>

            {healthMetrics.stockDiscrepancies.length > 0 && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col span={24}>
                        <Card title="Recent Stock Discrepancies" size="small">
                            <Table
                                dataSource={healthMetrics.stockDiscrepancies}
                                pagination={false}
                                size="small"
                                scroll={{ y: 200 }}
                                rowKey={(record, index) => `unique-key-${index}`}
                                columns={[
                                    { title: 'Item', dataIndex: 'item', key: 'item', width: 200, ellipsis: true },
                                    { title: 'Action', dataIndex: 'action', key: 'action', width: 150 },
                                    { title: 'Expected', dataIndex: 'expected', key: 'expected', width: 80 },
                                    { title: 'Actual', dataIndex: 'actual', key: 'actual', width: 80 },
                                    { title: 'Difference', dataIndex: 'difference', key: 'difference', width: 100, render: (value) => <Tag color={value > 10 ? 'red' : value > 5 ? 'orange' : 'yellow'}>{Math.round(value * 100) / 100}</Tag> },
                                    { title: 'Date', dataIndex: 'date', key: 'date', width: 100, render: (date) => moment(date).format('MMM DD') }
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};
export default function StockTransactions() {
    const [clients, setClients] = useState([]);
    const [result, setResult] = useState({ data: [], page: {} });
    const [showVolumeAnalytics, setShowVolumeAnalytics] = useState(false);
    const [showMovementAnalytics, setShowMovementAnalytics] = useState(false);
    const [showHealthAnalytics, setShowHealthAnalytics] = useState(false);
    const [showPerformanceAnalytics, setShowPerformanceAnalytics] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    // ── NEW: active category tab ─────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('all');
    const sdataRef = useRef({ p: 1, ps: 25 });
    const isNational = util.isNational();

    // ── Derive unique category tabs from loaded data ──────────────────────────
    const categoryTabs = useMemo(() => {
        const cats = [...new Set(result.data.map(v => v.cat).filter(Boolean))].sort();
        return cats;
    }, [result.data]);

    // ── Filter table + analytics data by selected tab ─────────────────────────
    const filteredData = useMemo(() => {
        if (activeTab === 'all') return result.data;
        return result.data.filter(v => v.cat === activeTab);
    }, [result.data, activeTab]);

    const list = () => {
        setHasSearched(true);
        setActiveTab('all'); // reset to All on every new search
        util.showLoader();
        
        ReportService.stockTransactions({ ...sdataRef.current, p: 1, ps: 1000 }).then(({ data }) => {
            const totalRecords = data.result.page.total_records;
            
            if (totalRecords > 0) {
                const allDataParams = {
                    ...sdataRef.current,
                    p: 1,
                    ps: totalRecords
                };
                
                return ReportService.stockTransactions(allDataParams);
            } else {
                return { data };
            }
        }).then(({ data }) => {
            setResult({ ...data.result });
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        });
    }

    const downloadPdf = () => {
        PdfReportsUtil.generateStockTransactionsReport(sdataRef, util.Orientation.LANSCAPE);
    }

    const download = () => {
        sdataRef.current.p = 1;
        sdataRef.current.ps = 0;
        util.showLoader();
        ReportService.stockTransactionsAll(sdataRef.current).then(({ data }) => {
            let header = {
                client: 'FACILITY',
                cat: 'CATEGORY',
                subcat: 'SUB CATEGORY',
                serial_no: 'SERIAL NUMBER',
                item_full_name: 'ITEM NAME',
                action: 'ACTION',
                stock_before: 'BEFORE CHANGE',
                qty: 'CHANGED QUANTITY',
                stock_after: 'AFTER CHANGE',
                txn_date: 'TXN DATE',
                created_by_user: 'CREATED BY',
            };

            let blob = util.convertToCSVBlob(data.result.data, header);
            saveAs(blob, "stock-transaction.csv");

        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    const exportAllAnalyticsPDF = () => {
        message.info('Generating comprehensive analytics PDF...');
        
        const analyticsData = {
            volumeAnalytics: showVolumeAnalytics,
            movementAnalytics: showMovementAnalytics,
            healthAnalytics: showHealthAnalytics,
            performanceAnalytics: showPerformanceAnalytics,
            transactionData: result.data
        };
        
        PdfReportsUtil.generateAnalyticsReport(analyticsData, 'Stock Transactions Analytics Report');
    };

    useEffect(() => {
        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => { return { id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me } });
            setClients(allClients);
        });

        return () => { message.destroy() }
    }, []);

    // ── Build antd Tabs items ────────────────────────────────────────────────
    const tabItems = useMemo(() => {
        return [
            {
                key: 'all',
                label: (
                    <span>
                        <AppstoreOutlined style={{ marginRight: 6 }} />
                        All Categories
                        {result.data.length > 0 && (
                            <Badge
                                count={result.data.length}
                                overflowCount={99999}
                                style={{ marginLeft: 8, backgroundColor: '#1890ff', fontSize: 11 }}
                            />
                        )}
                    </span>
                ),
            },
            ...categoryTabs.map(cat => {
                const count = result.data.filter(v => v.cat === cat).length;
                return {
                    key: cat,
                    label: (
                        <span>
                            {getCatIcon(cat)}
                            <span style={{ marginLeft: 6 }}>{cat}</span>
                            {count > 0 && (
                                <Badge
                                    count={count}
                                    overflowCount={99999}
                                    style={{
                                        marginLeft: 8,
                                        backgroundColor: cat.toLowerCase().includes('vehicle') ? '#fa8c16' : '#52c41a',
                                        fontSize: 11
                                    }}
                                />
                            )}
                        </span>
                    ),
                };
            }),
        ];
    }, [categoryTabs, result.data]);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Data Capturing Report</h2>
                </div>
                <div className="my-auto ml-auto">
                    <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                        <div className="d-flex align-items-center">
                            <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                                <BarChartOutlined /> Volume Analytics
                            </span>
                            <Switch 
                                checked={showVolumeAnalytics} 
                                onChange={setShowVolumeAnalytics}
                                size="small"
                            />
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                                <ArrowUpOutlined /> Movement Analytics
                            </span>
                            <Switch 
                                checked={showMovementAnalytics} 
                                onChange={setShowMovementAnalytics}
                                size="small"
                            />
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                                <DashboardOutlined /> Health Analytics
                            </span>
                            <Switch 
                                checked={showHealthAnalytics} 
                                onChange={setShowHealthAnalytics}
                                size="small"
                            />
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                                <ClockCircleOutlined /> Performance Analytics
                            </span>
                            <Switch 
                                checked={showPerformanceAnalytics} 
                                onChange={setShowPerformanceAnalytics}
                                size="small"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-pad">

                <div className="d-flex tbl-search-head">
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm 
                            dataRef={sdataRef} 
                            onSearch={list} 
                            clients={clients}
                            isNational={isNational}
                        />
                    </div>
                    <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={download} 
                            style={{
                                backgroundColor: 'green', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={excel} alt="download" height={15} /> Export Excel
                        </button>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={downloadPdf} 
                            style={{
                                backgroundColor: 'red', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={acrobat} alt="download" height={15} /> Export PDF
                        </button>
                    </div>
                </div>

                {/* ── Empty state before first search ──────────────────────── */}
                {!hasSearched && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '60px 20px', 
                        color: '#666',
                        fontSize: '16px'
                    }}>
                        <div style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '500' }}>
                            Stock Transactions Report
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            Click the search button to load transaction data and view analytics
                        </div>
                        <div style={{ fontSize: '14px', color: '#999' }}>
                            Use the filters above to narrow down your results
                        </div>
                    </div>
                )}

                {/* ── Category Tabs — only shown after data is loaded ───────── */}
                {hasSearched && result.data.length > 0 && (
                    <Tabs
                        activeKey={activeTab}
                        onChange={key => setActiveTab(key)}
                        items={tabItems}
                        size="middle"
                        style={{ marginBottom: 0 }}
                        tabBarStyle={{
                            marginBottom: 0,
                            background: '#fafafa',
                            paddingLeft: 8,
                            borderBottom: '2px solid #e8e8e8'
                        }}
                    />
                )}

                {/* ── Analytics panels — scoped to filteredData ────────────── */}
                {hasSearched && filteredData.length > 0 && (showVolumeAnalytics || showMovementAnalytics || showHealthAnalytics || showPerformanceAnalytics) && (
                    <div style={{ paddingTop: 16 }}>
                        {showVolumeAnalytics && (
                            <Card style={{ marginBottom: 16 }} size="small">
                                <TransactionVolumeAnalytics 
                                    isVisible={showVolumeAnalytics}
                                    data={filteredData} 
                                />
                            </Card>
                        )}

                        {showMovementAnalytics && (
                            <Card 
                                title={<span><ArrowUpOutlined style={{ marginRight: '8px' }} />Stock Movement Efficiency Analytics</span>} 
                                style={{ marginBottom: 16 }}
                                size="small"
                            >
                                <StockMovementAnalytics 
                                    data={filteredData} 
                                    isVisible={showMovementAnalytics}
                                />
                            </Card>
                        )}

                        {showHealthAnalytics && (
                            <Card 
                                title={<span><DashboardOutlined style={{ marginRight: '8px' }} />Inventory Health Analytics</span>} 
                                style={{ marginBottom: 16 }}
                                size="small"
                            >
                                <InventoryHealthAnalytics 
                                    data={filteredData} 
                                    isVisible={showHealthAnalytics}
                                />
                            </Card>
                        )}

                        {showPerformanceAnalytics && (
                            <Card 
                                title={<span><ClockCircleOutlined style={{ marginRight: '8px' }} />Data Capturer Performance Analytics</span>} 
                                style={{ marginBottom: 16 }}
                                size="small"
                            >
                                <DataCapturerPerformanceAnalytics 
                                    data={filteredData} 
                                    isVisible={showPerformanceAnalytics}
                                />
                            </Card>
                        )}
                    </div>
                )}

                {/* ── Transaction count summary ─────────────────────────────── */}
                {hasSearched && result.data.length > 0 && (
                    <div className="text-secondary mb8" style={{ padding: '8px 0 4px' }}>
                        Showing <strong>{filteredData.length}</strong> of <strong>{result.data.length}</strong> transactions
                        {activeTab !== 'all' && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>{activeTab}</Tag>
                        )}
                        {result.data.length > 100 && (
                            <span className="ml10" style={{ color: '#1890ff', fontSize: '12px' }}>
                                All data loaded for comprehensive analytics
                            </span>
                        )}
                    </div>
                )}

                {/* ── Transaction table — filtered by tab ──────────────────── */}
                <If cond={hasSearched && filteredData.length > 0}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover1 m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w20">SN</th>
                                    {isNational === 1 && <th className="w100">Facility</th>}
                                    <th className="w80">Category</th>
                                    <th className="w80">Sub Cat</th>
                                    <th className="w80">Serial No.</th>
                                    <th>Item</th>
                                    <th className="w100">Action</th>
                                    <th className="w80 text-center">Before Change</th>
                                    <th className="w80 text-center">Changed Qty</th>
                                    <th className="w80 text-center">After Change</th>
                                    <th className="w80">Txn Date</th>
                                    <th className="w100">Created By</th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {filteredData.map((v, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}.</td>
                                        {isNational === 1 && <td>{v.client}</td>}
                                        <td>{v.cat}</td>
                                        <td>{v.subcat}</td>
                                        <td>{v.serial_no}</td>
                                        <td>{v.item_full_name}</td>
                                        <td className="nowrap">{v.action}</td>
                                        <td className="text-right pr10">{v.stock_before}</td>
                                        <td className={"text-right pr10 " + (v.qty < 0 ? 'bg-warning' : 'bg-success font-white')}>{v.qty}</td>
                                        <td className="text-right pr10">{v.stock_after}</td>
                                        <td>{util.getDate(v.txn_date, 'DD MMM YYYY')}</td>
                                        <td title={v.created_by_role}>{v.created_by_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <div style={{ 
                            padding: '16px', 
                            textAlign: 'center', 
                            color: '#666',
                            fontStyle: 'italic',
                            width: '100%'
                        }}>
                            {filteredData.length} transaction{filteredData.length !== 1 ? 's' : ''} {activeTab !== 'all' ? `in ${activeTab}` : 'across all categories'}
                        </div>
                    </div>
                </If>

                <If cond={hasSearched && result.data.length === 0}>
                    <div className="no-rec">No records found with current filters</div>
                </If>

                <If cond={hasSearched && result.data.length > 0 && filteredData.length === 0}>
                    <div className="no-rec">
                        No transactions found for <strong>{activeTab}</strong>
                    </div>
                </If>

            </div>
        </div>
    )
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, isNational } = props;
    let [data, setData] = useState({ ...dataRef.current });
    const [items, setItems] = useState([]);
    
    const handleChange = (v, k) => {
        data[k] = v;
        setData({ ...data });
    }
    
    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        let ob = clients.find(v => v.is_me);
        setData({ ...data, client_id: ob?.id });
    }, [clients]);

    useEffect(() => {
        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    useEffect(() => {
        if (isNational === 1) {
            setData({ ...data, client_id: null });
        } else {
            let ob = clients.find(v => v.is_me);
            setData({ ...data, client_id: ob?.id });
        }
    }, [clients]);

    useEffect(() => {
        AssetService.allItems({ status: 1 }).then(res => {
            let allItems = res.data.result.data.map((v) => {
                let item_full_name = v.item_full_name + ' [' + (v.cat_type === 'Infrastructure' ? v.subcat : v.cat) + ']';
                return { label: item_full_name, value: v.id };
            });
            setItems(allItems);
        });
    }, []);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 &&
                    <div className="w150 mr5">
                        <AntdSelect
                            placeholder="All Facilities"
                            allowClear
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => {
                                data.item_id = null;
                                handleChange(v, 'client_id');
                            }}
                        />
                    </div>
                }

                <div className="w200 mr5">
                    <AntdSelect 
                        placeholder="Action (All)"
                        allowClear
                        options={[
                            { id: 1, title: 'Added By National' },
                            { id: 2, title: 'Received From Facility' },
                            { id: 3, title: 'Transfer To Facility' },
                            { id: 7, title: 'Issued To Department' },
                            { id: 9, title: 'Transfer Cancelled' },
                        ]} 
                        value={data.action_id} 
                        onChange={v => handleChange(v, 'action_id')} 
                    />
                </div>

                <div className="flex-grow-1 mr5">
                    <AntdSelect 
                        placeholder="Item (All)" 
                        allowClear 
                        showSearch 
                        sort
                        options={items} 
                        value={data.item_id} 
                        onChange={v => handleChange(v, 'item_id')} 
                    />
                </div>

                <div className="w130 mr5">
                    <AntdDatepicker 
                        placeholder="From Date"
                        allowClear
                        value={data.from_date} 
                        onChange={v => {
                            if (data.to_date && moment(v) > moment(data.to_date)) {
                                message.error("From date should be less than or equal to To Date");
                                v = null;
                            }
                            handleChange(v, 'from_date');
                        }} 
                    />
                </div>

                <div className="w130 mr5">
                    <AntdDatepicker 
                        placeholder="To Date"
                        allowClear
                        value={data.to_date} 
                        onChange={v => {
                            if (data.from_date && moment(v) < moment(data.from_date)) {
                                message.error("To date should be greater than or equal to From Date");
                                v = null;
                            }
                            handleChange(v, 'to_date');
                        }}
                    />
                </div>

                <div>
                    <Button type="primary" onClick={() => onSearch()}>
                        <img src={search} alt='' height={14}/>
                    </Button>
                </div>
            </div>
        </form>
    )
}